import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { differenceInCalendarDays, format } from 'date-fns'
import { computed } from 'mobx'
import { type Job } from '@/features/jobs/Job'
import { timeago } from '@/utils/date-util'
import { links } from '@/utils/link'
import { isNotNil } from '@/utils/object-util'
import { statusGlyph } from '@/features/jobs/job-status-display'
import { type SearchableJob } from '@/features/jobs/list/SearchableJob'
import { type JobTicket } from '@/features/jobs/tickets/JobTicket'
import { clientName, statusLabel } from '@/features/jobs/list/job-list-columns'

/**
 * Getter for the currently active job.
 */
export type GetActiveJobFn = () => Job | null | undefined

/**
 * View model for a job item in the list.
 */
export class JobItemViewModel {
  constructor(
    public readonly job: SearchableJob,
    private readonly _getActiveJob: GetActiveJobFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getLatestOpenTicket: () => JobTicket | null,
  ) {}

  /**
   * Whether we need to render workspace information.
   */
  @computed
  get isWorkspaceInfoRelevant() {
    // Only show the workspace information if the user is a member
    // of more than one workspace.
    return this._getCurrentUserWorkspaceMemberships().length > 1
  }

  /**
   * Collaborators on the job that are not you.
   */
  @computed
  get collaborators() {
    return this.job.members.filter((m) => !m.isCurrentUser)
  }

  /**
   * Names of the other collaborators, or "just you".
   */
  @computed
  get collaboratorNames() {
    return (
      this.collaborators
        .map((c) => c.displayName)
        .filter(isNotNil)
        .join(', ') || 'just you'
    )
  }

  /**
   * Link to the job.
   */
  @computed
  get href() {
    return this.job.id ? links.job(this.job.id) : ''
  }

  /**
   * The glyph showing where the job stands.
   */
  @computed
  get statusGlyph() {
    return statusGlyph(this.job)
  }

  /**
   * Gets the unread count.
   */
  @computed
  get unreadCount() {
    return this.job.conversation ? this.job.conversation.unreadCount : 0
  }

  /**
   * The last activity timestamp.
   */
  @computed
  get lastActivity() {
    return timeago(this.job.dateModified!)
  }

  /**
   * Whether this is the currently displayed job.
   */
  @computed
  get isActive() {
    const activeJobId = this._getActiveJob()?.id

    return activeJobId === this.job.id
  }

  /**
   * Returns the job name. If the job doesn't have a custom name,
   * it returns the computed title from the legend, else the legend name
   */
  @computed
  get jobName() {
    return this.job.name || this.job.description || this.job.legendName
  }

  /**
   * The client to show for the job, or its name when it has no client.
   */
  @computed
  get clientDisplayName() {
    return clientName(this.job) || this.jobName
  }

  /**
   * Whether the job is still open past its deadline.
   */
  @computed
  get isOverdue() {
    const days = this._daysUntilDue
    return days !== null && days < 0 && this.job.status !== 'Closed'
  }

  /**
   * How long until the job is due, or its last activity once there is no
   * deadline left to watch.
   */
  @computed
  get dueLabel() {
    const due = this.job.dateDeadline
    if (!due || this.job.status === 'Closed') {
      return this.lastActivity
    }
    const days = this._daysUntilDue
    return days !== null && days < 0 ? `${-days}d over` : format(due, 'MMM d')
  }

  /**
   * The job name and status, for the line under the client.
   */
  @computed
  get subtitle() {
    return [this.jobName, statusLabel(this.job)].filter(Boolean).join(' · ')
  }

  /**
   * Returns the latest open ticket for the job.
   */
  @computed
  get latestOpenTicket() {
    return this._getLatestOpenTicket()
  }

  private get _daysUntilDue() {
    const due = this.job.dateDeadline
    return due ? differenceInCalendarDays(due, new Date()) : null
  }
}
